import { Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FlexiComplianceService } from './flexi-compliance.service';
import { FlexiComplianceHistoryItem } from './flexi-compliance.models';

/**
 * Compact horizontal strip showing the last few completed compliance
 * reviews for the business — used above the section list on the CSM
 * rating page and on the customer final view. Each chip carries the
 * smiley + percentage + completion date.
 *
 * Chips are selectable: clicking one asks the host to show that review
 * read-only in place, and clicking the current one (or the already-selected
 * one) returns to it — so comparing against a past result never means a trip
 * through the history grid.
 *
 * Self-contained: takes a businessId input, fetches its own history.
 * Renders nothing when the business has zero or one completed review
 * (no meaningful "previous" to compare against).
 */
@Component({
  selector: 'flexi-compliance-history-strip',
  templateUrl: './history-strip.component.html',
  styleUrls: ['./flexi-compliance.scss'],
  standalone: false
})
export class FlexiComplianceHistoryStripComponent implements OnInit, OnChanges {
  @Input() businessId!: number;

  /** Cap shown in the strip; older ones aren't surfaced inline. */
  @Input() max = 5;

  /**
   * Id of the review the host is currently showing, so the strip can mark it.
   * Null means the host is on the live review.
   */
  @Input() selectedId: number | null = null;

  /**
   * Emits the review to show, or null to go back to the live one. The strip
   * only announces intent — the host owns loading and rendering, because only
   * it knows whether it can render read-only in place.
   */
  @Output() reviewSelected = new EventEmitter<FlexiComplianceHistoryItem | null>();

  rows: FlexiComplianceHistoryItem[] = [];
  loading = false;
  error: string | null = null;

  constructor(private service: FlexiComplianceService) {}

  /**
   * Toggles: picking the chip already being shown, or the current review's own
   * chip, returns to the live review rather than reloading the same thing.
   */
  select(row: FlexiComplianceHistoryItem): void {
    if (row.isCurrent || row.id === this.selectedId) {
      this.reviewSelected.emit(null);
      return;
    }
    this.reviewSelected.emit(row);
  }

  ngOnInit(): void { this.load(); }
  ngOnChanges(c: SimpleChanges): void {
    if (c['businessId'] && !c['businessId'].firstChange) this.load();
  }

  private load(): void {
    if (!this.businessId) return;
    this.loading = true;
    this.error = null;
    // Fetch a few extra so "View all" math is meaningful; cap render to `max`.
    this.service.history(this.businessId, Math.max(this.max + 1, 10)).subscribe({
      next: rows => {
        this.rows = rows;
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        // Soft-fail: don't block the parent page if history can't load.
        this.loading = false;
        this.error = err.error?.detail ?? null;
      }
    });
  }

  get visibleRows(): FlexiComplianceHistoryItem[] {
    return this.rows.slice(0, this.max);
  }

  /**
   * Hide the strip entirely until there are at least 2 completed reviews —
   * a single chip wouldn't convey anything you can't already see at the
   * top of the page.
   */
  get shouldShow(): boolean {
    return !this.loading && this.rows.length > 1;
  }

  toneClass(s: string): string {
    return `is-${s}`;
  }
}
